import { ActivityStatus, RegistrationStatus, ScheduledActivityOwner, ShareTokenModelResponse, UserRole } from "@fieldday/fielddayportal-model"
import { RegistrationContactStatus } from "@fieldday/fielddayportal-model/dist/src/model/RegistrationStatus"
import _ from "lodash"
import { ReactNode, createContext, useContext, useEffect, useState } from "react"
import { OrgUnitShare, ScheduledActivityForView } from "../models/Activity"
import { OrgUnit, TeamWithCount } from "../models/OrgUnit"
import { Registration } from "../models/Registration"
import { saIsPast } from "../util/dateUtil"
import { WEBIP_CURRENT_ID, isNonVolunteerEvent } from "../util/scheduledActivityUtils"
import { renderUrlTemplate } from "../util/stringUtil"
import { useAPI } from "../util/useAPI"
import { useTeamRoleContext } from "./teamRoleContext"
import { useAuth } from "./useAuth"

type ContextProps = {
  children: ReactNode,
  scheduledActivity: ScheduledActivityForView,
  orgUnitShares?: OrgUnitShare[],
  includeRegistrations?: boolean,
  refreshScheduledActivity?: () => Promise<void>,
}

type DataForContext = {
  scheduledActivity: ScheduledActivityForView,
  registrations: Required<Registration>[],
  guestRegistrations: Required<Registration>[],
  updateRegistration: (registration: Required<Registration>) => void,
  refreshRegistrations: () => Promise<void>,
  refreshScheduledActivity: () => Promise<void>,
  userGuestTokens: ShareTokenModelResponse[],
  otherGuestTokens: ShareTokenModelResponse[],
  refreshGuestTokens: (orgUnitId?: string) => Promise<void>,
  loggedInUserRegistration?: Required<Registration>,
  activeRegistration: boolean,
  showSurveyRegistration: boolean,
  isTeamPoc: boolean,
  isNpoPoc: boolean,
  teamOwned: boolean,
  teamOwnedCoordinator: boolean,
  isPastStart: boolean,
  isPastEnd: boolean,
  orgUnitShares?: OrgUnitShare[],
  coordinatorOfSharingTeams: OrgUnit[],
  allSharingTeams: TeamWithCount[], // including Teams you don't belong to
  fullParticipantCount: number,
  registrationLink?: string,
  ouIdForTeamMessagesAndPhotos?: string
}

const ScheduledActivityContext = createContext<DataForContext | undefined>(undefined)

export function ScheduledActivityProvider(
  { scheduledActivity, orgUnitShares, includeRegistrations, children,
    refreshScheduledActivity = async () => Promise.resolve(),
  }: ContextProps
) {
  const FieldDayAPI = useAPI()
  const { canCoordinate: canCoordinateTeam } = useTeamRoleContext()
  const [ registrations, setRegistrations ] = useState<Array<Required<Registration>>>([])
  // when fetching registrations, we can also see other Teams that have shared this
  const [ sharingOrgUnits, setSharingOrgUnits ] = useState<Array<TeamWithCount>>([])
  const [ fullParticipantCount, setFullParticipantCount ] = useState<number>(0)
  const [ guestRegistrations, setGuestRegistrations ] = useState<Array<Required<Registration>>>([])
  const [ userGuestTokens, setUserGuestTokens ] = useState<ShareTokenModelResponse[]>([])
  const [ otherGuestTokens, setOtherGuestTokens ] = useState<ShareTokenModelResponse[]>([])
  const { user, isWizard } = useAuth()
  const isOrgMember = (orgUnitId?: string) => {
    if(orgUnitId) {
      return user?.orgUnitMemberships.some(m => m.orgUnit.id === orgUnitId)
    } else {
      return user?.orgUnitMemberships.some(m => m.orgUnit.id === scheduledActivity.orgUnitId)
    }
  }

  const myReg = [...registrations, ...guestRegistrations].find(r => r.userId === user?.id)
  const ouIdForTeamMessagesAndPhotos = (() => {
    if (scheduledActivity.id === WEBIP_CURRENT_ID || isNonVolunteerEvent(scheduledActivity)) {
      return myReg?.orgUnitId ? myReg.orgUnitId : undefined
    } else {
      return undefined
    }
  })()

  const coordinatorOfSharingTeams: OrgUnit[] = (orgUnitShares ?? [])
    .filter(ous => user?.orgUnitMemberships
      .some(m => m.orgUnit.id === ous.orgUnitId && [UserRole.Coordinator, UserRole.Owner].includes(m.role as UserRole))
    ).map(m => m.orgUnit)

  const refreshRegistrations = async () => {
    if (!user) return
    // set this to 2000/page to support a WeBIP-sized event
    const resp = await FieldDayAPI.listRegistrations(scheduledActivity.id, { perPage: 2000 })
    const registrations = resp.registrations.filter(r => !r.guestTokenId)
    setRegistrations(registrations)

    const guestRegistrations = resp.registrations.filter(r => !!r.guestTokenId)
    setGuestRegistrations(guestRegistrations)

    const counts = resp.countsByOrgUnits ?? []
    // map team IDs with counts and cut out the teams that have 0
    setSharingOrgUnits((resp.orgUnits ?? [])
      .map(ou => {
        const cnt = counts.find(c => c.orgUnitId === ou.id)
        return { ...ou, participantCount: cnt?.participantCount ?? 0 }
      })
      .filter(t => t.participantCount && t.participantCount > 0)
    )
    // the counts will include the non-team as well with a null orgUnitId
    setFullParticipantCount(_.sumBy(counts, (c) => c.participantCount ))
  }

  const refreshGuestTokens = async (orgUnitId?: string) => {
    if (isOrgMember(orgUnitId) || isWizard()) {
      const resp = await FieldDayAPI.listScheduledActivityGuestLinks(scheduledActivity.id, orgUnitId)
      const guestTokens: ShareTokenModelResponse[] = resp.guestTokens

      const userGuestTokens = guestTokens.filter(t => t.userId === user?.id)
      setUserGuestTokens(userGuestTokens)

      const allGuestTokens = guestTokens.filter(t => t.userId !== user?.id)
      setOtherGuestTokens(allGuestTokens)
    }
  }

  const updateRegistration = (newRegistration: Required<Registration>) => {
    const registrationInd = registrations.findIndex(reg => reg.id === newRegistration.id)
    if (registrationInd >= 0) {
      // If the registration exists, update it with the new registration.
      registrations[registrationInd] = newRegistration
    } else {
      // Otherwise add the registration to the list.
      registrations.push(newRegistration)
    }
    setRegistrations(registrations)
  }

  const registrationLink = (): string | undefined => {
    if (user && scheduledActivity.registrationLink) {
      return renderUrlTemplate(scheduledActivity.registrationLink, {
        userEmail: user.email,
        userId: user.id,
        eventId: scheduledActivity.id,
      })
    }
    return
  }

  const loggedInUserRegistration = user ? [...registrations, ...guestRegistrations].find(r => r.userId === user.id) : undefined
  const activeRegistration = !!loggedInUserRegistration && [
    RegistrationStatus.Registered,
    RegistrationStatus.Waiting,
    RegistrationStatus.ReviewComplete
  ].includes(loggedInUserRegistration.status)
  const showSurveyRegistration = !!loggedInUserRegistration && [
    RegistrationStatus.Registered,
    RegistrationStatus.ReviewComplete,
  ].includes(loggedInUserRegistration.status)
  const isTeamPoc = registrations.find(r => r.userId === user?.id)?.contactStatus === RegistrationContactStatus.TeamContact
  const isNpoPoc = registrations.find(r => r.userId === user?.id)?.status === RegistrationStatus.Point_Of_Contact
  const teamOwned = scheduledActivity.owner === ScheduledActivityOwner.OrgUnit && !!isOrgMember
  const teamOwnedCoordinator = teamOwned && canCoordinateTeam()

  useEffect(() => {
    if ((scheduledActivity.status === ActivityStatus.Published || scheduledActivity.status === ActivityStatus.Canceled)
          && includeRegistrations) {
      refreshRegistrations()
    }
    return () => { }
  }, [scheduledActivity.id, includeRegistrations])

  const context: DataForContext = {
    scheduledActivity,
    registrations,
    guestRegistrations,
    updateRegistration,
    refreshRegistrations,
    refreshScheduledActivity,
    userGuestTokens,
    otherGuestTokens,
    refreshGuestTokens,
    loggedInUserRegistration,
    activeRegistration,
    showSurveyRegistration,
    isTeamPoc,
    isNpoPoc,
    teamOwned: teamOwned,
    teamOwnedCoordinator: teamOwnedCoordinator,
    isPastStart: saIsPast(scheduledActivity),
    isPastEnd: saIsPast(scheduledActivity, true),
    orgUnitShares,
    coordinatorOfSharingTeams,
    allSharingTeams: sharingOrgUnits,
    fullParticipantCount: fullParticipantCount,
    registrationLink: registrationLink(),
    ouIdForTeamMessagesAndPhotos,
  }

  return (
    <ScheduledActivityContext.Provider value={context}>
      {children}
    </ScheduledActivityContext.Provider>
  )
}

export const useScheduledActivityContext = () => {
  const context = useContext(ScheduledActivityContext)
  if (!context) throw new Error('useScheduledActivityContext must be used within a ScheduledActivityProvider')
  return context
}
